import React from 'react';
import type {ComboboxItem} from '@mantine/core';
import {Select, TextInput} from '@mantine/core';
import {Flex} from 'src/design/atoms/Flex';
import type {
  ModuleEnum,
  NotificationRuleReminderCondition,
} from 'src/api/ProactApiClient';
import {RemovableCard} from 'src/design/organisms/RemovableCard';
import {
  GetReminderConditionPropertyOptions,
  getReminderConditionPropertyConfig,
  getReminderConditionPropertyDescription,
} from '../notificationRuleHelpers';

export interface EditNotificationReminderRuleProps {
  module: ModuleEnum;
  data: NotificationRuleReminderCondition;
  index: number;
  disabled: boolean;
  onChange: (data: NotificationRuleReminderCondition, index: number) => void;
  onDelete: (data: NotificationRuleReminderCondition, index: number) => void;
  classificationOptions?: ComboboxItem[];
  attachmentTypeOptions?: ComboboxItem[];
}
export const EditNotificationReminderRule: React.FC<
  EditNotificationReminderRuleProps
> = ({module, data, index, disabled, onChange, onDelete, classificationOptions, attachmentTypeOptions}) => {
  const reminderConditionPropertyOptions =
    GetReminderConditionPropertyOptions(module);

  const propertySelectData = reminderConditionPropertyOptions.map(opt => ({
    value: String(opt.id),
    label: opt.label,
  }));

  const propertyConfig = getReminderConditionPropertyConfig(data.property);

  const operatorSelectData = propertyConfig.operators.map(opt => ({
    value: String(opt.id),
    label: opt.label,
  }));

  function handlePropertyChange(value: string | null): void {
    if (!value) return;
    onChange({...data, property: Number(value)}, index);
  }

  function handleOperatorChange(value: string | null): void {
    if (!value) return;
    onChange({...data, operator: Number(value)}, index);
  }

  function handleValueChange(event: React.ChangeEvent<HTMLInputElement>): void {
    onChange({...data, value: event.target.value}, index);
  }

  function handleSelectValueChange(value: string | null): void {
    onChange({...data, value: value ?? ''}, index);
  }

  return (
    <RemovableCard
      index={index}
      onDelete={() => onDelete(data, index)}
      disabled={disabled}
      description={getReminderConditionPropertyDescription(data.property)}>
      <Flex
        direction={{base: 'column', lg: 'row'}}
        gap="md"
        align={{base: 'stretch', lg: 'flex-end'}}
        w="100%">
        <Select
          label="Property"
          data={propertySelectData}
          value={String(data.property)}
          onChange={handlePropertyChange}
          disabled={disabled}
          style={{flex: 2}}
        />
        <Select
          label="Operator"
          data={operatorSelectData}
          value={String(data.operator)}
          onChange={handleOperatorChange}
          disabled={disabled}
          style={{flex: 1}}
        />
        {propertyConfig.valueInputKind === 'classification' ? (
          <Select
            label="Value"
            data={classificationOptions ?? []}
            value={data.value ?? null}
            onChange={handleSelectValueChange}
            disabled={disabled}
            searchable
            clearable
            style={{flex: 2}}
          />
        ) : propertyConfig.valueInputKind === 'attachmentType' ? (
          <Select
            label="Value"
            data={attachmentTypeOptions ?? []}
            value={data.value ?? null}
            onChange={handleSelectValueChange}
            disabled={disabled}
            style={{flex: 2}}
          />
        ) : (
          <TextInput
            label="Value"
            value={data.value ?? ''}
            onChange={handleValueChange}
            disabled={disabled}
            style={{flex: 2}}
          />
        )}
      </Flex>
    </RemovableCard>
  );
};
